import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { serviceApi } from '../services/api';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';

interface Service {
  id: string;
  storeId: string;
  name: string;
  description?: string;
  price: number;
  duration: number;
  category?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ServiceState {
  services: Service[];
  selectedService: Service | null;
  isLoading: boolean;
  hasFetched: boolean; // 標記是否已完成過一次讀取
  error: string | null;
  showDeleteConfirmModal: boolean;
  serviceToDeleteId: string | null;

  // Actions
  fetchServices: (storeId: string) => Promise<void>;
  createService: (storeId: string, data: any) => Promise<void>;
  updateService: (id: string, data: any) => Promise<void>;
  toggleServiceStatus: (id: string, isActive: boolean) => Promise<void>;
  handleDelete: (id: string) => void;
  confirmDelete: () => Promise<void>;
  closeDeleteModal: () => void;
  setSelectedService: (service: Service | null) => void;
}

export const useServiceStore = create<ServiceState>()(
  persist(
    (set, get) => ({
  services: [],
  selectedService: null,
  isLoading: false,
  hasFetched: false,
  error: null,
  showDeleteConfirmModal: false,
  serviceToDeleteId: null,

  fetchServices: async (storeId: string) => {
    const { services, hasFetched } = get();
    const hasExistingData = services.length > 0;

    // 只有在首次讀取且無資料時才顯示載入狀態
    // hasFetched 確保「讀取後無資料」的情況不會重複顯示載入
    set({ isLoading: !hasExistingData && !hasFetched, error: null });

    try {
      const response = await serviceApi.getByStore(storeId);
      set({ services: response.data, isLoading: false, hasFetched: true });
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.fetchFailed');
      set({ error: errorMsg, isLoading: false, hasFetched: true });
      toast.error(errorMsg);
    }
  },

  createService: async (storeId: string, data: any) => {
    // 樂觀更新：立即新增臨時項目
    const tempId = `temp-${Date.now()}`;
    const optimisticService: Service = {
      id: tempId,
      storeId,
      name: data.name,
      description: data.description,
      price: data.price,
      duration: data.duration,
      category: data.category,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    set((state) => ({
      services: [...state.services, optimisticService],
    }));
    toast.success(i18n.t('common.messages.createSuccess'));

    // 背景 API（不 await，讓 Dialog 立即關閉）
    serviceApi.create(storeId, data)
      .then((response) => {
        set((state) => ({
          services: state.services.map((s) => s.id === tempId ? response.data : s),
        }));
      })
      .catch((error: any) => {
        set((state) => ({
          services: state.services.filter((s) => s.id !== tempId),
        }));
        const errorMsg = error.response?.data?.message || i18n.t('common.messages.createFailed');
        toast.error(errorMsg);
      });
  },

  updateService: async (id: string, data: any) => {
    // 1. 保存舊值
    const originalServices = get().services;

    // 2. 樂觀更新
    set((state) => ({
      services: state.services.map((s) => s.id === id ? { ...s, ...data } : s),
    }));

    try {
      const response = await serviceApi.update(id, data);
      // 3. 用真實資料替換
      set((state) => ({
        services: state.services.map((s) => s.id === id ? response.data : s),
      }));
      toast.success(i18n.t('common.messages.updateSuccess'));
    } catch (error: any) {
      // 4. 失敗回滾
      set({ services: originalServices });
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
      toast.error(errorMsg);
      throw error;
    }
  },

  toggleServiceStatus: async (id: string, isActive: boolean) => {
    // 樂觀更新
    const previousServices = get().services;
    set((state) => ({
      services: state.services.map((s) =>
        s.id === id ? { ...s, isActive } : s
      ),
    }));

    try {
      await serviceApi.toggleStatus(id, isActive);
      toast.success(i18n.t('common.messages.updateSuccess'));
    } catch (error: any) {
      // 失敗時回滾
      set({ services: previousServices });
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
      toast.error(errorMsg);
      throw error;
    }
  },

  handleDelete: (id: string) => {
    set({ showDeleteConfirmModal: true, serviceToDeleteId: id });
  },

  confirmDelete: async () => {
    const { serviceToDeleteId } = get();
    if (!serviceToDeleteId) return;

    // 樂觀更新：立即關閉 Modal + 移除項目
    const previousServices = get().services;
    set((state) => ({
      services: state.services.filter((s) => s.id !== serviceToDeleteId),
      showDeleteConfirmModal: false,
      serviceToDeleteId: null,
    }));

    try {
      await serviceApi.delete(serviceToDeleteId);
      toast.success(i18n.t('common.messages.deleteSuccess'));
    } catch (error: any) {
      // 失敗時回滾
      set({ services: previousServices });
      const errorMsg = error.response?.data?.message || i18n.t('common.messages.deleteFailed');
      toast.error(errorMsg);
    }
  },

  closeDeleteModal: () => {
    set({
      showDeleteConfirmModal: false,
      serviceToDeleteId: null
    });
  },

  setSelectedService: (selectedService) => set({ selectedService }),
}),
    {
      name: 'service-storage',
      partialize: (state) => ({
        services: state.services,
        hasFetched: state.hasFetched,
      }),
    }
  )
);
